import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { registerPasskey, passkeySupported, inIframe } from "@/lib/passkey";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Fingerprint, ShieldCheck, LogOut, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

export default function SetupPasskey() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [nickname, setNickname] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const add = async () => {
    if (inIframe()) {
      toast.error("Passkeys can't be created inside the embedded preview. Open the app in its own browser tab first.");
      return;
    }
    if (!passkeySupported()) return toast.error("This device/browser does not support passkeys.");
    setBusy(true);
    try {
      await registerPasskey(nickname || "My passkey");
      toast.success("Passkey added");
      await refresh();
      navigate("/");
    } catch (err) {
      if (err.name === "NotAllowedError") toast.error("Passkey setup was cancelled or timed out.");
      else if (err.name === "SecurityError") toast.error("Passkeys require the app on its own secure (HTTPS) domain.");
      else toast.error(formatApiErrorDetail(err.response?.data?.detail || err.message) || "Could not add passkey");
    } finally {
      setBusy(false);
    }
  };

  const disableRequirement = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/settings", { enforce_passkey_enrollment: false });
      toast.success("Passkey requirement disabled");
      await refresh();
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update setting");
    } finally {
      setBusy(false);
    }
  };

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9FAFB]">
      <div className="w-full max-w-sm" data-testid="setup-passkey-page">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/renova-icon.png" alt="" className="w-10 h-10 object-contain" />
          <span className="font-heading font-extrabold text-lg">Renova Eco Systems</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#1B4332]" />
          <h2 className="font-heading text-2xl font-bold tracking-tight">Set up your passkey</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          For your security, a passkey (Touch ID, Face ID, Windows Hello or a security key) is required before you can use the app.
        </p>

        {inIframe() && (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 mb-4" data-testid="setup-iframe-warning">
            <TriangleAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-800">
              Passkeys can't be created inside this embedded preview.
              <button onClick={() => window.open(window.location.href, "_blank", "noopener")}
                className="ml-1 font-semibold underline hover:no-underline" data-testid="setup-open-new-tab">
                Open in a new tab
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Input value={nickname} onChange={(e) => setNickname(e.target.value)}
            placeholder="Passkey name (e.g. My iPhone)" data-testid="setup-passkey-nickname" />
          <Button onClick={add} disabled={busy} data-testid="setup-add-passkey-btn"
            className="w-full bg-[#0A0A0A] hover:bg-gray-800 h-11">
            <Fingerprint className="w-4 h-4 mr-2" /> {busy ? "Waiting…" : "Create passkey"}
          </Button>
        </div>

        {isAdmin && (
          <button type="button" onClick={disableRequirement} disabled={busy} data-testid="setup-disable-requirement"
            className="w-full mt-4 text-xs text-gray-400 hover:text-gray-700 underline">
            Admin: disable the passkey requirement for now
          </button>
        )}

        <button type="button" onClick={doLogout} data-testid="setup-logout"
          className="w-full mt-3 h-10 flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-800">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </div>
  );
}
